import { SSOEnforcementType } from 'shared/enums';
import { SetupFlowMode } from './enums';
import type {
  BackupCodesConfig,
  SetupFlowConfig,
  SSOEnforcementOption,
} from './types';

export const LOW_CODES_THRESHOLD = 3;
export const TOTP_CELL_COUNT = 6;

export const SETUP_FLOW_CONFIG: Record<SetupFlowMode, SetupFlowConfig> = {
  [SetupFlowMode.Configure]: {
    backLabel: 'Configure Two-Factor Authentication',
    description: 'Use your authenticator app to scan the QR code.',
    verifyButtonLabel: 'Verify and Continue',
  },
  [SetupFlowMode.ChangeDevice]: {
    backLabel: 'Change Authenticator App',
    description: 'Use your authenticator app to scan the QR code.',
    verifyButtonLabel: 'Verify and Update',
  },
};

export const BACKUP_CODES_CONFIG: Record<SetupFlowMode, BackupCodesConfig> = {
  [SetupFlowMode.Configure]: {
    backLabel: 'Configure Two-Factor Authentication',
    description:
      'These backup codes can be used to access your account if you lose access to your authenticator app.',
    footerButtonLabel: 'Finish Set Up',
  },
  [SetupFlowMode.ChangeDevice]: {
    backLabel: 'Backup Codes',
    description:
      'Use these codes to access your account if you lose your device. Each code can only be used once. If you regenerate new codes, your old codes will no longer work.',
    footerButtonLabel: 'Regenerate Backup Codes',
  },
};

export const BACKUP_CODES_MESSAGES = {
  NoCodes:
    "You don't have any unused backup codes left. Please generate a new set of backup codes.",
  SaveWarning:
    'Previously generated backup codes are no longer valid. Save your new codes in a secure place.',
  RegenerateSuccess: 'New backup codes generated successfully.',
  RegenerateError: 'Unable to generate backup codes. Please try again.',
};

export const SETUP_FLOW_MESSAGES = {
  ChangeDeviceSuccess:
    'Two-Factor Authentication settings updated successfully.',
};

export const SSO_ENFORCEMENT_MESSAGES = {
  SaveSuccess: 'SSO settings updated successfully.',
  SaveError: 'Failed to change SSO Enforcement Setting. Please try again.',
  UnsavedChangesWarning:
    'You have unsaved changes in SSO Enforcement settings. Please save or discard your changes before switching tabs.',
  SelectDomainRequired:
    'To save this option, please select a domain first.',
  NoActiveUsers: (domains: string[]) =>
    `No active users present in the domain(s): ${domains.join(', ')}.`,
};

export const SSO_ENFORCEMENT_OPTIONS: Record<
  SSOEnforcementType,
  SSOEnforcementOption
> = {
  [SSOEnforcementType.None]: {
    label: 'None',
    description:
      'SSO is optional. Users can sign in using email and password or SSO.',
  },
  [SSOEnforcementType.Everyone]: {
    label: 'Everyone',
    description:
      'All users must sign in using SSO. Email and password login will be disabled.',
  },
  [SSOEnforcementType.SelectedDomains]: {
    label: 'Selected Domains',
    description: 'Users with selected email domains must sign in using SSO.',
  },
};
